/**
 * Wishlist stored in this browser (localStorage key "th-wishlist").
 * <s-wishlist-button data-handle>: toggles a trek in the wishlist.
 * <s-wishlist-list data-products-url>: renders saved treks on the account page
 * by cloning its <template> and filling it from each product's .js endpoint.
 */
const KEY = 'th-wishlist';
const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch {
    return [];
  }
};
const write = (list) => {
  localStorage.setItem(KEY, JSON.stringify(list));
  document.dispatchEvent(new CustomEvent('wishlist:change'));
};

class WishlistButton extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const handle = this.dataset.handle;
      const list = read();
      write(list.includes(handle) ? list.filter((h) => h !== handle) : [...list, handle]);
    }, { signal });
    document.addEventListener('wishlist:change', () => this.#sync(), { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #sync() {
    if (!this.button) return;
    const on = read().includes(this.dataset.handle);
    this.button.setAttribute('aria-pressed', String(on));
    this.button.setAttribute('aria-label', on ? this.button.dataset.labelRemove : this.button.dataset.labelAdd);
  }
}

class WishlistList extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    document.addEventListener('wishlist:change', () => this.#render(), { signal: this.#abort.signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  async #render() {
    const list = this.querySelector('[data-wishlist-items]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const tpl = this.querySelector('template');
    const handles = read();
    const base = this.dataset.productsUrl || '';
    empty.hidden = handles.length > 0;
    list.replaceChildren();
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: this.dataset.currency || 'INR', maximumFractionDigits: 0 });
    const products = await Promise.all(handles.map((h) => fetch(`${base}/${encodeURIComponent(h)}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
    products.filter(Boolean).forEach((p) => {
      const node = tpl.content.cloneNode(true);
      node.querySelectorAll('[data-w-link]').forEach((a) => { a.href = p.url; });
      const img = node.querySelector('[data-w-img]');
      if (p.featured_image) img.src = `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=400`;
      else img.remove();
      node.querySelector('[data-w-title]').textContent = p.title;
      const priceEl = node.querySelector('[data-w-price]');
      const labelEl = node.querySelector('[data-w-price-label]');
      const ranged = p.price_max > p.price_min && p.price_min > 0;
      priceEl.textContent = p.price_min > 0 ? (ranged ? `${formatter.format(p.price_min / 100)} – ${formatter.format(p.price_max / 100)}` : formatter.format(p.price_min / 100)) : '';
      if (labelEl) labelEl.textContent = ranged ? labelEl.dataset.range : labelEl.dataset.single;
      const btn = node.querySelector('[data-w-remove]');
      btn.addEventListener('click', () => write(read().filter((h) => h !== p.handle)));
      list.append(node);
    });
    empty.hidden = list.children.length > 0;
  }
}

if (!customElements.get('s-wishlist-button')) customElements.define('s-wishlist-button', WishlistButton);
if (!customElements.get('s-wishlist-list')) customElements.define('s-wishlist-list', WishlistList);
